import { useEffect, useState } from "react";
import type { FC, MouseEvent } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import Image from "next/image";
import { colours, device } from "@helpers";
import {
	helpsiteTrackButton,
	helpsiteTrackCta,
} from "@helpers/helpsite-mixpanel";
import Icon from "@components/Icon";

const DesktopSupportFooterContainer = styled.div`
	display: none;

	@media (min-width: ${device.laptop}) {
		display: flex;
		flex-direction: column;
		gap: 60px;
		padding: 60px 0px 0px;
		width: 100%;
		max-width: 1136px;
		margin: 0 auto;
	}
`;

const DesktopFAQCTA = styled.div`
	background-color: ${colours.PALE_PEACH};
	border-radius: 20px;
	padding: 0;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: stretch;
	overflow: hidden;
`;

const SupportImageWrapper = styled.div`
	display: flex;
	width: 50%;
	flex: 0 0 50%;
	align-self: stretch;
`;

const SupportImageContainer = styled.div`
	width: 100%;
	height: 100%;
	min-height: 360px;
	position: relative;
`;

const SupportContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 30px;
	padding: 40px 56px;
	width: 50%;
	box-sizing: border-box;
	justify-content: flex-start;
`;

const SupportHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const SupportTitle = styled.h2`
	font-family: var(--euclid-medium);
	font-weight: 500;
	font-size: 30px;
	line-height: 1.268000030517578em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
`;

const SupportSubtitle = styled.h3`
	font-family: var(--euclid-medium);
	font-weight: 500;
	font-size: 22px;
	line-height: 1.267999995838512em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
`;

const ContactTimes = styled.div`
	display: flex;
	flex-direction: column;
	gap: 21px;
`;

const ContactTimesText = styled.p`
	font-family: var(--euclid-regular);
	font-size: 16px;
	line-height: 1.2680000066757202em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
	width: 100%;
`;

const SupportTimesLink = styled.a`
	color: ${colours.MIDNIGHT_BLUE};
	text-decoration: underline;
	cursor: pointer;
	font-family: var(--euclid-regular);
	font-size: 16px;
	line-height: 1.2680000066757202em;
	pointer-events: auto;
	position: relative;
	z-index: 1;

	&:hover {
		text-decoration: underline;
		opacity: 0.8;
	}

	&:visited {
		color: ${colours.MIDNIGHT_BLUE};
	}

	&:active {
		opacity: 0.6;
	}
`;

const ExpandedSupportTimes = styled.div<{ isOpen: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
	box-sizing: border-box;
	margin-top: ${(props) => (props.isOpen ? "10px" : "0")};
	background-color: ${colours.WHITE};
	position: relative;
	padding: 15px;
	border-radius: 8px;
	overflow: hidden;
	max-height: ${(props) => (props.isOpen ? "160px" : "0")};
	opacity: ${(props) => (props.isOpen ? 1 : 0)};
	transform: translateY(${(props) => (props.isOpen ? "0" : "-4px")});
	pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};
	transition:
		max-height 240ms ease,
		opacity 200ms ease,
		transform 200ms ease,
		margin-top 200ms ease;

	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(255, 235, 231, 0.5);
		border-radius: 8px;
		pointer-events: none;
		z-index: 0;
	}

	& > * {
		position: relative;
		z-index: 1;
	}
`;

const ExpandedSupportTimesText = styled.p`
	font-family: var(--euclid-regular);
	font-size: 16px;
	line-height: 1.2680000066757202em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
`;

const ChatButtonContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: 100%;
`;

const ChatButton = styled.button`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 6px;
	padding: 5px 20px;
	background-color: ${colours.MIDNIGHT_BLUE};
	border-radius: 12px;
	border: 2px solid transparent;
	cursor: pointer;
	width: 320px;
	transition: background-color 0.2s;
	box-sizing: border-box;

	&:hover {
		background-color: ${colours.WHITE};
		border-color: ${colours.MIDNIGHT_BLUE};
	}
`;

const ChatButtonText = styled.span`
	font-family: var(--euclid-regular);
	font-size: 18px;
	line-height: 1.2679999669392903em;
	color: ${colours.WHITE};
	transition: color 0.2s;

	${ChatButton}:hover & {
		color: ${colours.MIDNIGHT_BLUE};
	}
`;

const ChatButtonIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	
	img {
		transition: filter 0.2s;
		/* Make icon white initially */
		filter: brightness(0) invert(1);
	}
	
	${ChatButton}:hover & img {
		/* Make icon midnight blue on hover */
		filter: brightness(0) saturate(100%) invert(15%) sepia(20%) saturate(2000%) hue-rotate(200deg) brightness(0.9);
	}
`;

type FreshchatWidget = { show: () => void; open: () => void };

type SupportStatus = {
	isHumanOnline: boolean;
	closingTimeLabel: string;
};

const getSydneySupportStatus = (): SupportStatus => {
	try {
		const now = new Date();
		const formatter = new Intl.DateTimeFormat("en-AU", {
			timeZone: "Australia/Sydney",
			hour12: false,
			hour: "numeric",
		});

		const parts = formatter.formatToParts(now);
		const hourPart = parts.find((p) => p.type === "hour");
		const hour = hourPart ? Number(hourPart.value) : 0;

		const isHumanOnline = hour >= 8 && hour < 20;

		return { isHumanOnline, closingTimeLabel: "8PM" };
	} catch {
		return { isHumanOnline: true, closingTimeLabel: "8PM" };
	}
};

const HelpDesktopSupportFooter: FC = () => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];
	const [showSupportTimes, setShowSupportTimes] = useState(false);
	const [supportStatus, setSupportStatus] = useState<SupportStatus | null>(
		null,
	);

	useEffect(() => {
		setSupportStatus(getSydneySupportStatus());
	}, []);

	const handleSupportTimesClick = (e: MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setShowSupportTimes((prev) => {
			const next = !prev;
			helpsiteTrackButton({
				helpsite_section: "footer_desktop",
				page_path: pagePath,
				label: "Support times",
				cta_type: next ? "expand" : "collapse",
			});
			return next;
		});
	};

	const handleChatButtonClick = () => {
		helpsiteTrackCta({
			helpsite_section: "footer_desktop",
			page_path: pagePath,
			label: "Chat with us online",
			cta_type: "freshchat_open",
		});
		if (typeof window !== "undefined") {
			const fcWidget = (window as Window & { fcWidget?: FreshchatWidget })
				.fcWidget;
			if (fcWidget) {
				fcWidget.show();
				fcWidget.open();
			}
		}
	};

	return (
		<DesktopSupportFooterContainer>
			<DesktopFAQCTA>
				<SupportImageWrapper>
					<SupportImageContainer>
						<Image
							src="/images/helpsite_need_help.jpg"
							alt="Support"
							fill
							style={{
								objectFit: "cover",
							}}
						/>
					</SupportImageContainer>
				</SupportImageWrapper>
				<SupportContent>
					<SupportHeader>
						<SupportTitle>Need help? </SupportTitle>
						<SupportSubtitle>Chat with us</SupportSubtitle>
						<ContactTimes>
							<ContactTimesText>
								{(supportStatus?.isHumanOnline ?? true) ? (
									<>
										Our Australian based support team is online until{" "}
										{supportStatus?.closingTimeLabel ?? "8PM"} today (Sydney
										time).{" "}
										<SupportTimesLink
											href="#"
											onClick={handleSupportTimesClick}
										>
											Support times
										</SupportTimesLink>
									</>
								) : (
									<>
										Our Australian based support team is online 7 days 8am-8pm
										(Sydney time).
									</>
								)}
							</ContactTimesText>
							<ExpandedSupportTimes
								isOpen={showSupportTimes}
								aria-hidden={!showSupportTimes}
							>
								<ExpandedSupportTimesText>
									Our Australian based support team is online 7 days 8am-8pm
									(Sydney time).
								</ExpandedSupportTimesText>
							</ExpandedSupportTimes>
						</ContactTimes>
					</SupportHeader>
					<ChatButtonContainer>
						<ChatButton onClick={handleChatButtonClick}>
							<ChatButtonIcon>
								<Icon
									iconName="chat"
									iconSizes={{
										iconMobile: 40,
										iconTablet: 40,
										iconLaptop: 40,
										iconDesktop: 40,
										iconDesktopLarge: 40,
									}}
									alt="Chat icon"
								/>
							</ChatButtonIcon>
							<ChatButtonText>Chat with us online</ChatButtonText>
						</ChatButton>
					</ChatButtonContainer>
				</SupportContent>
			</DesktopFAQCTA>
			{/* <FurtherComms>
				<FurtherCommsContent>
					<FurtherCommsTitle>Got ideas or suggestions?</FurtherCommsTitle>
					<FurtherCommsText>
						We'd love to hear them, leave your feedback here
					</FurtherCommsText>
				</FurtherCommsContent>
			</FurtherComms> */}
		</DesktopSupportFooterContainer>
	);
};

export default HelpDesktopSupportFooter;
